"use client"

import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/16/solid"
import clsx from "clsx"
import Spinner from "../Spinner"

export type StepSize = "sm" | "md"

type ProgressStepsProps<TStage extends string> = {
  stages: readonly TStage[]
  stageLabels: Record<TStage, string>
  displayStage: TStage
  displayIndex: number
  isError: boolean
  isSuccess: boolean
  size: StepSize
}

const hiddenClasses = "scale-0 opacity-0 blur-xs"
const transitionClasses = "transition duration-200 ease-in-out"

export function ProgressSteps<TStage extends string>({
  stages,
  stageLabels,
  displayStage,
  displayIndex,
  isError,
  isSuccess,
  size,
}: ProgressStepsProps<TStage>) {
  return (
    <ol>
      {stages.map((stage, i) => {
        const isCurrent = displayStage === stage
        const isPast = displayIndex > i
        const isLast = i === stages.length - 1
        const status = isError ? "error" : isSuccess ? "success" : "pending"
        const label =
          isLast && isCurrent && isError ? "Failed" : stageLabels[stage]

        return (
          <li
            key={stage}
            className={clsx("flex", {
              "gap-4": size === "md",
              "gap-3": size === "sm",
            })}
          >
            <div className="flex flex-col items-center">
              {/* Icons */}
              <div
                className={clsx(
                  "relative flex shrink-0 items-center justify-center",
                  {
                    "size-6 [&>svg]:size-5": size === "md",
                    "size-5 [&>svg]:size-4": size === "sm",
                  }
                )}
              >
                <div
                  className={clsx(
                    "-outline-offset-2 absolute rounded-full outline-2 outline-gray-300 dark:outline-gray-500",
                    transitionClasses,
                    (isPast || isCurrent) && hiddenClasses,
                    {
                      "size-3": size === "sm",
                      "size-4": size === "md",
                    }
                  )}
                />

                <CheckCircleIcon
                  className={clsx(
                    "absolute text-green-600 dark:text-green-500",
                    transitionClasses,
                    !isPast &&
                      !(isCurrent && status === "success") &&
                      hiddenClasses
                  )}
                />

                <div
                  className={clsx(
                    "absolute",
                    transitionClasses,
                    !(isCurrent && status === "pending") && hiddenClasses
                  )}
                >
                  <Spinner
                    size={size}
                    className="text-gray-900 dark:text-white"
                  />
                </div>

                <XCircleIcon
                  className={clsx(
                    "absolute text-red-500 dark:text-red-400",
                    transitionClasses,
                    !(isCurrent && status === "error") && hiddenClasses
                  )}
                />
              </div>

              {/* Line between steps */}
              {!isLast && (
                <div
                  className={clsx(
                    "w-0.5 flex-1 rounded-full",
                    transitionClasses,
                    isPast
                      ? "bg-gray-900 dark:bg-white"
                      : "bg-gray-300 dark:bg-gray-500"
                  )}
                />
              )}
            </div>

            {/* Label */}
            <div
              className={clsx(
                "text-pretty font-semibold tracking-tight",
                transitionClasses,
                isCurrent || isPast
                  ? "text-gray-900 dark:text-white"
                  : "text-gray-400 dark:text-gray-500",
                {
                  "text-sm/5": size === "sm",
                  "text-base/6": size === "md",
                  "pb-6": size === "md" && !isLast,
                  "pb-5": size === "sm" && !isLast,
                }
              )}
            >
              {label}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
